import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

interface PlanFeature {
  label: string;
  value: string | number;
}

interface PlanDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  features: {
    leads: number;
    calls: number;
    whatsapp: number;
    emails: number;
    linkedin: number;
    followUps?: number;
    dashboards?: number;
    countries?: number | string;
    industries?: number | string;
    products?: number | string;
    languages?: number | string;
    userLogins?: number;
    support?: string;
    crmIntegration?: string;
  };
}

export function PlanDetailsModal({ isOpen, onClose, planName, features }: PlanDetailsModalProps) {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">{planName} Package</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Leads</span>
              <span className="text-gray-900 font-semibold">{features.leads.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Calls</span>
              <span className="text-gray-900 font-semibold">{features.calls.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">WhatsApp Msgs</span>
              <span className="text-gray-900 font-semibold">{features.whatsapp.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Emails</span>
              <span className="text-gray-900 font-semibold">{features.emails.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">LinkedIn Actions</span>
              <span className="text-gray-900 font-semibold">{features.linkedin.toLocaleString()}</span>
            </div>
            {features.followUps !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Follow-Ups</span>
                <span className="text-gray-900 font-semibold">{features.followUps}</span>
              </div>
            )}
            {features.dashboards !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Dashboard{features.dashboards > 1 ? 's' : ''}</span>
                <span className="text-gray-900 font-semibold">{features.dashboards}</span>
              </div>
            )}
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-extrabold text-[#05E0D1] font-display mb-4">Targeting & Customization</h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-4">
              {features.dashboards !== undefined && features.dashboards > 1 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">Dashboards</span>
                    <span className="text-gray-900 font-semibold">{features.dashboards}</span>
                  </div>
                </div>
              )}
              {features.countries !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">{typeof features.countries === 'number' && features.countries > 1 ? 'Countries' : 'Country'}</span>
                    <span className="text-gray-900 font-semibold">{features.countries}</span>
                  </div>
                </div>
              )}
              {features.industries !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">Industries</span>
                    <span className="text-gray-900 font-semibold">{features.industries}</span>
                  </div>
                </div>
              )}
              {features.products !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">{typeof features.products === 'number' && features.products > 1 ? 'Products' : 'Product'}</span>
                    <span className="text-gray-900 font-semibold">{features.products}</span>
                  </div>
                </div>
              )}
              {features.languages !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">{typeof features.languages === 'number' && features.languages > 1 ? 'Languages' : 'Language'}</span>
                    <span className="text-gray-900 font-semibold">{features.languages}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-extrabold text-[#05E0D1] font-display mb-4">Access & Support</h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-4">
              {features.userLogins !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">User Logins</span>
                    <span className="text-gray-900 font-semibold">{features.userLogins}</span>
                  </div>
                </div>
              )}
              {features.support !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">Support</span>
                    <span className="text-gray-900 font-semibold">{features.support}</span>
                  </div>
                </div>
              )}
              {features.crmIntegration !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#05E0D1]" />
                  <div className="flex justify-between items-center flex-1">
                    <span className="text-gray-700 font-medium">CRM Integration</span>
                    <span className="text-gray-900 font-semibold">{features.crmIntegration}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
